/**
 * Canonical player type family shared by the web and mobile apps, moved here
 * from `apps/web/src/types/Player.ts` (plus the small types it depended on)
 * for the Compare Players feature. The web modules that used to define these
 * (`types/Player.ts`, `functions/getPriceDisplay.ts`,
 * `functions/calculateChemistry.ts`, `redux/evolutions/evolutionsSlice.ts`,
 * `api/privateRequests/squad-builder/get-meta-ratings.ts`) now re-export from
 * this module.
 *
 * NOTE: `redux/players/playersSlice.ts` exports its own `Player`/`MetaRating`
 * (the mobile list shapes, with required fields) through the redux barrel.
 * This family is deliberately exported ONLY via `@repo/shared/types` so the
 * two never collide in a barrel; the optional-field `MetaRating` here is the
 * detailed-endpoint variant.
 */
/**
 * The coin value a player contributes to a squad/club total. Only `market` and
 * `sbc` resolve to coins; token sources expose a token *count* and
 * objective/extinct have no coin value, so they contribute `0` — preserving the
 * pre-`priceInfo` behaviour of summing the raw market/sbc price.
 */
export const getCoinValue = (priceInfo) => priceInfo && (priceInfo.source === "market" || priceInfo.source === "sbc")
    ? priceInfo.displayPrice
    : 0;
/** How long one cell of the sprite strip is held. */
export const FOIL_FRAME_MS = 90;
// A card's cosmetics in the order given, then its foil, as EA draws them.
// See be ADR 0035.
export const cardEffects = (foil, cosmetics = []) => [
    // Cosmetic strips start and end empty, so a pass jumps from and back to
    // EA's static image.
    ...cosmetics.map(({ static: still, animated, frames = 0 }) => ({
        still,
        cells: 1,
        strip: animated && frames > 1 ? { animated, frames } : undefined,
    })),
    ...(foil && foil.frames >= 1
        ? [
            {
                still: foil.animated,
                cells: foil.frames,
                strip: foil.frames > 1 ? foil : undefined,
            },
        ]
        : []),
];
// A frame gap longer than this means the frame loop stalled.
const FOIL_STALL_MS = 50;
// How long a screen must draw without a stall before a card's first pass.
const FOIL_CALM_MS = 1000;
// Resolves on the first frame after FOIL_CALM_MS without a stall. A pass
// started while the screen is still loading stutters under that work.
export const foilCalm = () => new Promise((resolve) => {
    let last;
    let calm = 0;
    const frame = (now) => {
        const gap = now - (last ?? now);
        last = now;
        calm = gap > FOIL_STALL_MS ? 0 : calm + gap;
        if (calm >= FOIL_CALM_MS)
            resolve(now);
        else
            requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
});
/**
 * The halo a foil card casts, traced from the card art's own silhouette.
 *
 * Two lobes, because the halo we are matching — futwiz's — does not fall off
 * like one blur. They paint theirs into the sprite's own alpha, so it reads
 * pixel by pixel: 89 one pixel out, 34 at ten, 10 at twenty, still 4 at thirty.
 * No single shadow is both that tight against the edge and that long in the
 * tail. A dim wide lobe with a bright narrow one over it fits that curve to
 * ~6%; our old single magenta blur sat at 27 out where theirs is 10, which is
 * what made it read as spill rather than light.
 *
 * Violet rather than futbin's magenta, and the near lobe is nearly white —
 * only the tail carries real hue. Both sampled off futwiz's `items/holo.webp`.
 *
 * ORDER MATTERS: the wide lobe is drawn first and the narrow one re-blurs it,
 * which is where the long tail comes from. Web joins them into one `filter` in
 * this order; mobile nests a View per lobe, innermost first, for the same
 * reason. Swapping them loses the fit.
 *
 * Blur is in the card's own units — both apps draw the art at ~180 wide — so it
 * scales with the card the way futwiz's fixed pixels do not.
 */
export const FOIL_GLOW = [
    { color: "rgba(137, 117, 180, 0.06)", blur: 25 },
    { color: "rgba(180, 179, 252, 0.7)", blur: 6 },
];
export const isEvolution = (p) => ("evoPathId" in p && Boolean(p.evoPathId)) ||
    ("evolutionData" in p && Boolean(p.evolutionData)) ||
    // An in-progress build (e.g. staged from the evolution builder) carries
    // only the applied step path — no path id, no evolution metadata.
    ("evolutionPath" in p && Boolean(p.evolutionPath?.length));
export const isEvolutionPlayer = (p) => "basePlayer" in p && Boolean(p.basePlayer);
//# sourceMappingURL=player.js.map